import { RENDER_SCALE, WORLD_HEIGHT, WORLD_WIDTH } from '../render/render-scale';
import { buildWorld, roomLeft, ROOM_WIDTH, type WorldDefinition } from '../data/world';
import { ARENA_LAYOUT } from '../render/arena-layout';
import Phaser from 'phaser';
import { COLORS } from '../config/branding';
import { getFlavor } from '../data/flavors';
import { getFruit } from '../data/fruits';
import { LEVELS, type LevelPalette } from '../data/levels';
import { cloudPuff, mixColor } from '../render/cartoon';
import { ParticlePool, ProceduralRenderer } from '../render/ProceduralRenderer';
import { audioManager, inputManager, saveStore } from '../services';
import { GameSimulation } from '../sim/game-simulation';
import type { SettingsData } from '../sim/save';
import type { SimulationEvent } from '../sim/types';
import type { StartSelection } from '../ui/UIManager';
import { uiManager } from '../ui-instance';

const FIXED_STEP_MS = 1000 / 60;
/**
 * Longest wall-clock gap a single frame may replay, in fixed steps. Kept small
 * because a replayed step re-samples the same live key state, so a long
 * catch-up would stretch a quick tap into a held run.
 */
const MAX_CATCH_UP_STEPS = 3;
/** Long enough to read both mazes moving through their shared doorway. */
const ROOM_TRANSITION_MS = 420;
const BACKDROP_WIDTH = 1280;
const BACKDROP_HEIGHT = 720;
// `setScrollFactor(0)` removes camera scroll but intentionally keeps camera
// zoom. Our HiDPI camera is zoomed to match its larger backing store, so fixed
// layers need this one-time centring compensation to remain at screen (0, 0).
const BACKDROP_FIXED_X = WORLD_WIDTH * (RENDER_SCALE - 1) / 2;
const BACKDROP_FIXED_Y = WORLD_HEIGHT * (RENDER_SCALE - 1) / 2;

type CubicSegment = readonly [control1X: number, control1Y: number, control2X: number, control2Y: number, endX: number, endY: number];

/** Samples a chain of cubic curves into the rounded ridges of the skyline. */
function curvedPoints(
  startX: number,
  startY: number,
  segments: readonly CubicSegment[],
  samplesPerSegment = 10,
): Phaser.Math.Vector2[] {
  const points = [new Phaser.Math.Vector2(startX, startY)];
  let fromX = startX;
  let fromY = startY;
  for (const [control1X, control1Y, control2X, control2Y, endX, endY] of segments) {
    for (let step = 1; step <= samplesPerSegment; step += 1) {
      const time = step / samplesPerSegment;
      const inverse = 1 - time;
      points.push(new Phaser.Math.Vector2(
        inverse ** 3 * fromX + 3 * inverse ** 2 * time * control1X + 3 * inverse * time ** 2 * control2X + time ** 3 * endX,
        inverse ** 3 * fromY + 3 * inverse ** 2 * time * control1Y + 3 * inverse * time ** 2 * control2Y + time ** 3 * endY,
      ));
    }
    fromX = endX;
    fromY = endY;
  }
  return points;
}

/** Fills a ridge silhouette down to the bottom of the backdrop. */
function fillRidge(
  graphics: Phaser.GameObjects.Graphics,
  crest: readonly Phaser.Math.Vector2[],
  color: number,
  alpha = 1,
): void {
  const first = crest[0];
  const last = crest[crest.length - 1];
  if (!first || !last) return;
  graphics.fillStyle(color, alpha);
  graphics.fillPoints([
    new Phaser.Math.Vector2(first.x, BACKDROP_HEIGHT + 40),
    ...crest,
    new Phaser.Math.Vector2(last.x, BACKDROP_HEIGHT + 40),
  ], true);
}

/** A single storybook pine: stacked skirts, a trunk, and fresh snow on top. */
function drawPine(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  baseY: number,
  scale: number,
  body: number,
  snow: number,
): void {
  const width = 30 * scale;
  const height = 78 * scale;
  graphics.fillStyle(0x5b4630, 1);
  graphics.fillRect(x - 3 * scale, baseY - 12 * scale, 6 * scale, 14 * scale);
  for (let tier = 0; tier < 3; tier += 1) {
    const tierWidth = width * (1 - tier * 0.22);
    const tierBase = baseY - 8 * scale - tier * height * 0.27;
    const tierTop = tierBase - height * 0.46;
    graphics.fillStyle(body, 1);
    graphics.fillTriangle(x - tierWidth, tierBase, x + tierWidth, tierBase, x, tierTop);
    graphics.fillStyle(snow, 0.92);
    graphics.fillTriangle(
      x - tierWidth * 0.62,
      tierBase - height * 0.1,
      x + tierWidth * 0.62,
      tierBase - height * 0.1,
      x,
      tierTop + height * 0.06,
    );
  }
  graphics.fillStyle(snow, 1);
  graphics.fillCircle(x, baseY - height * 0.96, 4 * scale);
}

/** Deterministic snowflake used by both parallax snow layers. */
function drawBackdropFlake(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
  color: number,
  alpha: number,
): void {
  graphics.fillStyle(color, alpha);
  graphics.fillCircle(x, y, radius);
  if (radius < 3) return;
  graphics.fillStyle(color, alpha * 0.55);
  graphics.fillCircle(x, y, radius * 1.7);
}

export class GameScene extends Phaser.Scene {
  simulation!: GameSimulation;
  private worldRenderer!: ProceduralRenderer;
  private particles!: ParticlePool;
  private runData!: StartSelection;
  private accumulator = 0;
  private lastFrameMs = 0;
  private hitStopMs = 0;
  private world: WorldDefinition | null = null;
  private viewRoom = 0;
  private roomTween: Phaser.Tweens.Tween | null = null;
  private roomTransitionToken = 0;
  private ending = false;
  private debug = false;
  private backdropClouds: Phaser.GameObjects.Graphics[] = [];
  private backdropSnow: Phaser.GameObjects.Graphics[] = [];
  private backdropTweens: Phaser.Tweens.Tween[] = [];
  private backdropMotionReduced: boolean | null = null;

  constructor() {
    super('GameScene');
  }

  init(data: StartSelection): void {
    this.runData = {
      levelId: data.levelId ?? 1,
      players: data.players ?? 1,
      flavors: data.flavors ?? ['vanilla', 'strawberry'],
    };
    this.debug = new URLSearchParams(window.location.search).get('debug') === '1';
    this.accumulator = 0;
    this.lastFrameMs = 0;
    this.hitStopMs = 0;
    this.ending = false;
  }

  create(): void {
    // The campaign is one connected snowfield: every authored room stitched
    // together, entered at the room the player chose.
    this.world = buildWorld();
    const definition = this.world.level;
    this.simulation = new GameSimulation(definition, {
      players: this.runData.players,
      flavors: this.runData.flavors,
      difficulty: saveStore.data.settings.difficulty,
      invulnerable: false,
      world: this.world,
    });
    // The map's level select is a checkpoint into the snowfield.
    const startRoom = Math.max(0, Math.min(this.world.rooms.length - 1, this.runData.levelId - 1));
    this.simulation.enterRoom(startRoom);
    this.viewRoom = startRoom;
    this.cameras.main.setBackgroundColor(definition.palette.background);
    this.focusRoom(startRoom, true);
    this.createSkyBackdrop(definition.palette, definition.id);
    this.worldRenderer = new ProceduralRenderer(this, this.simulation);
    this.worldRenderer.showRoom(startRoom);
    this.particles = new ParticlePool(this, saveStore.data.settings.reducedMotion ? 28 : 72);
    this.showHUD();
    audioManager.startMusic(this.runData.levelId);
    // Fade up from bright snow rather than a dark void, matching the daylight art.
    this.cameras.main.fadeIn(saveStore.data.settings.reducedMotion ? 80 : 240, 255, 255, 255);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      audioManager.stopMusic();
      this.backdropTweens.forEach((tween) => tween.stop());
      this.backdropTweens = [];
      this.backdropClouds = [];
      this.backdropSnow = [];
      this.backdropMotionReduced = null;
      this.roomTween?.stop();
      this.roomTween = null;
      this.roomTransitionToken += 1;
      this.worldRenderer.destroy();
      this.particles.destroy();
    });
  }

  update(time: number, _delta: number): void {
    // Phaser smooths its own delta toward the target frame time, which makes a
    // 45 fps frame report as 16 ms and quietly runs the whole game in slow
    // motion. The fixed step is driven from the wall clock instead, so a cat
    // always crosses a tile in the same real time on any hardware.
    const elapsed = this.lastFrameMs === 0 ? FIXED_STEP_MS : time - this.lastFrameMs;
    this.lastFrameMs = time;
    const delta = Phaser.Math.Clamp(elapsed, 0, FIXED_STEP_MS * MAX_CATCH_UP_STEPS);
    if (inputManager.consumeRestart()) {
      this.fastRestart();
      return;
    }
    if (this.ending) {
      this.drawFrame(time);
      return;
    }
    if (inputManager.consumePause()) {
      this.togglePause();
      this.drawFrame(time);
      return;
    }
    if (inputManager.consumeJoin()) {
      if (this.simulation.players.length === 1) {
        const joined = this.simulation.addSecondPlayer(this.runData.flavors[1]);
        if (joined) {
          this.runData.players = 2;
          this.showHUD();
          uiManager.toast('P2 JOINED · REVIVE EACH OTHER', 'good');
        }
      } else if (this.simulation.removeSecondPlayer()) {
        this.runData.players = 1;
        this.showHUD();
        uiManager.toast('P2 LEFT THE SNOWGLOBE', 'warn');
      }
    }
    if (this.simulation.paused) {
      this.drawFrame(time);
      return;
    }
    if (this.hitStopMs > 0 && !saveStore.data.settings.reducedMotion) {
      this.hitStopMs -= delta;
      this.drawFrame(time);
      return;
    }
    this.accumulator = Math.min(this.accumulator + delta, FIXED_STEP_MS * MAX_CATCH_UP_STEPS);
    while (this.accumulator >= FIXED_STEP_MS) {
      const frames = inputManager.poll(this.simulation.players.length === 2 ? 2 : 1);
      const events = this.simulation.step(frames);
      events.forEach((event) => this.handleEvent(event));
      this.accumulator -= FIXED_STEP_MS;
      if (this.ending) break;
    }
    this.trackRoom();
    this.drawFrame(time);
  }

  private drawFrame(time: number): void {
    this.worldRenderer.draw(time, this.accumulator / FIXED_STEP_MS);
    uiManager.updateHUD(this.simulation);
  }

  debugCompleteObjectives(): void {
    this.simulation.completeObjectives();
    for (const event of [...this.simulation.events]) this.handleEvent(event);
  }

  debugCompleteFruitWave(): void {
    this.simulation.completeActiveFruitWave();
    for (const event of [...this.simulation.events]) this.handleEvent(event);
  }

  debugShift(): void {
    this.simulation.triggerShift('DEBUG SHIFT');
  }

  debugToggleInvulnerable(): void {
    this.simulation.invulnerable = !this.simulation.invulnerable;
    uiManager.toast(`INVULNERABLE ${this.simulation.invulnerable ? 'ON' : 'OFF'}`, 'warn');
  }

  debugWin(): void {
    this.simulation.debugFinish();
    for (const event of this.simulation.events) this.handleEvent(event);
  }

  debugThaw(): void {
    this.simulation.debugThawPlayer(1);
    for (const event of this.simulation.events) this.handleEvent(event);
  }

  debugPresentedRooms(): readonly number[] {
    return this.worldRenderer?.getPresentedRooms() ?? [this.viewRoom];
  }

  debugRoomTransitioning(): boolean {
    return this.roomTween !== null;
  }

  private showHUD(): void {
    uiManager.showHUD(this.simulation, inputManager, {
      onPause: () => this.togglePause(),
      onCompleteDebug: () => this.debugCompleteObjectives(),
      onFruitWaveDebug: () => this.debugCompleteFruitWave(),
      onShiftDebug: () => this.debugShift(),
      onInvulnerableDebug: () => this.debugToggleInvulnerable(),
    }, this.debug);
  }

  private togglePause(): void {
    this.simulation.paused = !this.simulation.paused;
    if (!this.simulation.paused) {
      uiManager.hidePause();
      return;
    }
    uiManager.showPause({
      onResume: () => this.togglePause(),
      onRestart: () => this.fastRestart(),
      onSettings: () => this.showPauseSettings(),
      onQuit: () => this.scene.start('MenuScene', { openSelect: true }),
    });
  }

  private showPauseSettings(): void {
    uiManager.showSettings(saveStore.data.settings, inputManager, {
      onChange: (settings: SettingsData) => {
        saveStore.data.settings = { ...settings, controls: { ...settings.controls } };
        inputManager.setBindings(settings.controls);
        audioManager.setVolumes(settings.musicVolume, settings.sfxVolume);
        document.body.classList.toggle('reduced-motion', settings.reducedMotion);
        document.body.classList.toggle('high-contrast', settings.highContrast);
        this.configureBackdropMotion(settings.reducedMotion);
        saveStore.persist();
      },
      onBack: () => {
        this.showHUD();
        this.simulation.paused = false;
        this.togglePause();
      },
    });
  }

  private fastRestart(): void {
    this.ending = true;
    this.scene.restart(this.runData);
  }

  private handleEvent(event: SimulationEvent): void {
    audioManager.playEvent(event, this.simulation.scoring.state.combo);
    const center = event.cell ? this.worldRenderer.cellCenter(event.cell) : { x: 640, y: 360 };
    const particleScale = saveStore.data.settings.reducedMotion ? 0.45 : 1;
    switch (event.type) {
      case 'move': {
        const player = this.simulation.players.find((candidate) => candidate.id === event.playerId);
        if (player) {
          this.particles.burst(
            center.x,
            center.y + 10,
            getFlavor(player.flavor).trail,
            saveStore.data.settings.reducedMotion ? 1 : 3,
            particleScale * 0.28,
          );
        }
        break;
      }
      case 'core':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          this.particles.burst(center.x, center.y, fruit.primary, 13, particleScale);
          this.particles.burst(center.x, center.y, fruit.secondary, 7, particleScale * 0.72);
          this.pickupRipple(center.x, center.y, fruit.primary);
          const waveIndex = this.simulation.fruitWaves.findIndex((wave) => wave.kind === fruit.id);
          uiManager.celebrateFruitPickup(Math.max(0, waveIndex));
          if (this.simulation.scoring.state.combo >= 3) this.punch(65, 0.0025);
          this.hitStopMs = this.simulation.scoring.state.combo >= 5 ? 28 : 18;
          uiManager.toast(`${fruit.name.toUpperCase()} · +${event.amount ?? 0}`);
        }
        break;
      case 'final-core':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          this.particles.burst(center.x, center.y, fruit.primary, 22, particleScale * 1.3);
          this.particles.burst(center.x, center.y, 0xffffff, 13, particleScale);
          this.pickupRipple(center.x, center.y, fruit.primary, true);
          uiManager.celebrateFruitPickup(this.simulation.activeFruitWaveIndex);
        }
        this.hitStopMs = 52;
        this.punch(170, 0.006);
        uiManager.toast(
          this.simulation.definition.boss && !this.simulation.portalOpen && this.simulation.bossPhase >= 2
            ? 'SHIELD HARVEST COMPLETE · WARDEN EXPOSED'
            : 'FINAL FRUIT · SNOWGATE CHARGING',
          'good',
        );
        break;
      case 'fruit-wave':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          this.particles.burst(center.x, center.y, fruit.primary, 24, particleScale * 1.25);
          this.particles.burst(center.x, center.y, fruit.secondary, 14, particleScale);
          this.hitStopMs = 46;
          this.punch(130, 0.0045);
          uiManager.celebrateFruitWave(this.simulation.activeFruitWaveIndex);
          uiManager.toast(
            `NEXT BATCH · ${fruit.plural.toUpperCase()} · +${event.amount ?? 0}`,
            'good',
            fruit.traitIcon,
            1_900,
          );
        }
        break;
      case 'fruit-warning':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          const count = event.fruitKind === 'pineapple' || event.fruitKind === 'cherries' ? 5 : 3;
          this.particles.burst(center.x, center.y - 5, fruit.secondary, count, particleScale * 0.35);
        }
        break;
      case 'fruit-move':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          const takeoff = event.label === 'TAKEOFF';
          this.particles.burst(
            center.x,
            center.y,
            takeoff ? fruit.secondary : fruit.primary,
            takeoff ? 8 : event.label === 'RUN' ? 5 : 3,
            particleScale * (takeoff ? 0.55 : 0.3),
          );
        }
        break;
      case 'fruit-teleport':
        {
          const fruit = getFruit(event.fruitKind ?? this.simulation.activeFruitWave.kind);
          this.particles.burst(center.x, center.y, 0xbda6ff, 9, particleScale * 0.55);
          this.particles.burst(center.x, center.y, fruit.primary, 5, particleScale * 0.34);
        }
        break;
      case 'wall-place':
        // Frost breathed out toward the new line.
        this.particles.burst(center.x, center.y, 0xffffff, 6, particleScale * 0.6);
        this.worldRenderer.breatheFrost(event.playerId ?? 1);
        break;
      case 'wall-reject':
        uiManager.toast(
          event.label === 'limit'
            ? 'PRESS SPACE TO REMOVE YOUR WALL'
            : 'NO ROOM FOR FROSTWALL',
          'warn',
        );
        break;
      case 'wall-crack':
        if (event.label === 'FROST STRIKE') {
          // The cat's blow connecting, before the ice starts giving way.
          this.particles.shatter(center.x, center.y, 0xd8f4ff, 6, particleScale * 0.55);
          this.hitStopMs = 26;
          this.punch(70, 0.0028);
        } else {
          this.particles.shatter(center.x, center.y, 0x9fe6ff, 4, particleScale * 0.7);
          uiManager.toast(event.label ?? 'FROSTWALL CRACKING', 'danger');
        }
        break;
      case 'wall-shatter':
        if (event.label === 'MELT') {
          // The cat's blow already landed on the first block. The rest of the
          // run comes apart on its own, so it must not shake the screen again
          // or a long line reads as the whole game juddering.
          this.particles.shatter(center.x, center.y, 0xbdefff, 7, particleScale * 0.7);
          if ((event.amount ?? 0) > 0) this.punch(90, 0.004);
          break;
        }
        this.particles.shatter(center.x, center.y, event.label === 'ENERGIZED' ? 0xd8c2ff : 0xbdefff, 9, particleScale);
        this.particles.burst(center.x, center.y, 0xffffff, 12, particleScale * 0.9);
        this.hitStopMs = 58;
        this.punch(120, 0.008);
        uiManager.toast(event.label === 'ENERGIZED' ? 'RAIL BREAK!' : 'FROST BURST', 'good');
        break;
      case 'enemy-alert':
        // `amount: 0` is a hunter standing down, which is relief, not a threat.
        uiManager.toast(event.label ?? 'ENEMY ALERT', event.amount === 0 ? 'good' : 'warn');
        if (event.amount === 1) {
          this.particles.burst(center.x, center.y - 18, 0xff2a3d, 8, particleScale * 0.6);
          this.punch(90, 0.003);
        }
        break;
      case 'shift-warning':
        uiManager.toast(event.label ?? 'SHIFT INCOMING', 'warn');
        break;
      case 'shift':
        this.shiftPulse(this.simulation.worldState === 'overdrive' ? COLORS.violet : COLORS.cyan);
        this.punch(220, 0.004);
        uiManager.toast(this.simulation.worldState === 'overdrive' ? 'OVERDRIVE · WALLS ARE RAILS' : 'NORMAL · WALLS SOLID', 'warn');
        break;
      case 'portal-open':
        this.particles.burst(center.x, center.y, COLORS.violet, 28, particleScale * 1.4);
        this.punch(260, 0.006);
        uiManager.toast('SNOWGATE OPEN · MOVE!', 'good');
        break;
      case 'damage':
        this.particles.burst(center.x, center.y, COLORS.danger, 13, particleScale);
        this.hitStopMs = 46;
        this.punch(150, 0.012);
        uiManager.toast(`${event.label ?? 'HIT'}!`, 'danger');
        break;
      case 'thaw':
        {
          const player = this.simulation.players.find((candidate) => candidate.id === event.playerId);
          const meltColor = player ? getFlavor(player.flavor).primary : COLORS.cyan;
          this.particles.burst(center.x, center.y - 5, 0xffffff, 12, particleScale * 0.9);
          this.particles.burst(center.x, center.y + 4, meltColor, 10, particleScale * 0.72);
          this.thawRipple(center.x, center.y + 10, meltColor);
          this.hitStopMs = 62;
          this.punch(190, 0.009);
          uiManager.toast(
            this.simulation.players.length === 2
              ? `P${event.playerId ?? 1} THAWED · STAY CLOSE TO REVIVE`
              : 'FROST SHELL RELEASED!',
            'danger',
          );
        }
        break;
      case 'revive': uiManager.toast(`P${event.playerId ?? 1} REVIVED`, 'good'); break;
      case 'special': uiManager.toast(event.label ?? 'SPECIAL', 'good'); break;
      case 'shard':
        this.particles.burst(center.x, center.y, 0xffffff, 22, particleScale);
        uiManager.toast('HIDDEN SUGAR SHARD!', 'good');
        break;
      case 'boss-phase':
        this.hitStopMs = 62;
        this.punch(340, 0.01);
        uiManager.toast(`WARDEN PHASE ${event.amount ?? 2} · ${event.label ?? ''}`, 'danger');
        break;
      case 'victory': this.completeRun(); break;
      case 'failure': this.failRun(event.label ?? 'LOST IN THE STORM'); break;
      default: break;
    }
  }

  /**
   * The view sits on one room at a time. Walking through a doorway slides the
   * camera to the next room rather than cutting, so the world reads as one
   * continuous place instead of a set of screens.
   */
  private focusRoom(room: number, immediate = false, fromRoom = room): void {
    const camera = this.cameras.main;
    camera.setZoom(RENDER_SCALE);
    const centreX = (roomLeft(room) + ROOM_WIDTH / 2) * ARENA_LAYOUT.tile + ARENA_LAYOUT.x;
    const centreY = WORLD_HEIGHT / 2;
    this.roomTween?.stop();
    this.roomTween = null;
    const transitionToken = ++this.roomTransitionToken;
    if (immediate || saveStore.data.settings.reducedMotion) {
      camera.centerOn(centreX, centreY);
      this.worldRenderer?.showRoom(room);
      return;
    }
    this.worldRenderer.showTransition(fromRoom, room);
    const destination = camera.getScroll(centreX, centreY);
    this.roomTween = this.tweens.add({
      targets: camera,
      // Phaser's scroll coordinates are based on the backing-store camera
      // size; zoom is applied later when it derives the world viewport. Using
      // a world-width divisor here made a 2x camera overshoot by half a screen.
      scrollX: destination.x,
      duration: ROOM_TRANSITION_MS,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (transitionToken !== this.roomTransitionToken || this.viewRoom !== room) return;
        this.roomTween = null;
        this.worldRenderer.showRoom(room);
      },
    });
  }

  private trackRoom(): void {
    const player = this.simulation.players.find((candidate) => !candidate.downed) ?? this.simulation.players[0];
    if (!player) return;
    // Start the panorama as soon as a tile crossing through the open arch is
    // committed. Waiting until the cat landed made the world react a beat late.
    const room = this.simulation.roomOf(player.moving ? player.target : player.cell);
    if (room === this.viewRoom) return;
    const fromRoom = this.viewRoom;
    this.viewRoom = room;
    this.focusRoom(room, false, fromRoom);
    const name = this.world?.rooms[room]?.definition.name;
    if (name) uiManager.toast(`SNOW TRAIL · ${name}`, 'good');
  }

  private punch(duration: number, intensity: number): void {
    const strength = saveStore.data.settings.shakeStrength;
    if (saveStore.data.settings.reducedMotion || strength <= 0) return;
    this.cameras.main.shake(duration, intensity * strength);
  }

  private pickupRipple(x: number, y: number, color: number, final = false): void {
    const ring = this.add.circle(x, y, final ? 14 : 9, 0xffffff, 0)
      .setStrokeStyle(final ? 4 : 3, color, 0.95)
      .setDepth(20);
    const duration = saveStore.data.settings.reducedMotion ? 90 : final ? 430 : 300;
    this.tweens.add({ targets: ring, scale: final ? 3.2 : 2.5, alpha: 0, duration, ease: 'Cubic.Out', onComplete: () => ring.destroy() });
  }

  private shiftPulse(color: number): void {
    const frame = this.add.rectangle(640, 360, 1_230, 670, color, 0.025)
      .setStrokeStyle(saveStore.data.settings.reducedMotion ? 5 : 11, color, 0.58)
      .setDepth(29)
      .setScale(0.94)
      .setAlpha(0.75);
    this.tweens.add({
      targets: frame,
      scale: 1.04,
      alpha: 0,
      duration: saveStore.data.settings.reducedMotion ? 90 : 260,
      ease: 'Cubic.Out',
      onComplete: () => frame.destroy(),
    });
  }

  private thawRipple(x: number, y: number, color: number): void {
    const puddle = this.add.ellipse(x, y, 18, 7, color, 0.48)
      .setStrokeStyle(2, 0xffffff, 0.7)
      .setDepth(19);
    this.tweens.add({
      targets: puddle,
      scaleX: 2.25,
      scaleY: 1.45,
      alpha: 0,
      duration: saveStore.data.settings.reducedMotion ? 150 : 430,
      ease: 'Cubic.Out',
      onComplete: () => puddle.destroy(),
    });
  }

  private completeRun(): void {
    if (this.ending || !this.simulation.result) return;
    this.ending = true;
    const result = this.simulation.result;
    saveStore.recordResult(result);
    this.time.delayedCall(saveStore.data.settings.reducedMotion ? 120 : 520, () => {
      audioManager.stopMusic();
      uiManager.showResults(result, result.levelId === LEVELS.length, {
        onReplay: () => this.scene.restart(this.runData),
        onNext: () => {
          const nextId = result.levelId === LEVELS.length ? 1 : result.levelId + 1;
          this.scene.restart({ ...this.runData, levelId: nextId });
        },
        onMap: () => this.scene.start('MenuScene', { openSelect: true }),
      });
    });
  }

  private failRun(label: string): void {
    if (this.ending) return;
    this.ending = true;
    // Keep the arena visible long enough for the readable frost-release pose.
    const revealDelay = saveStore.data.settings.reducedMotion ? 200 : 540;
    this.time.delayedCall(revealDelay, () => uiManager.showFailure(label, {
      onRestart: () => this.scene.restart(this.runData),
      onMap: () => this.scene.start('MenuScene', { openSelect: true }),
    }));
  }

  /**
   * Daylight snow diorama. Everything lives behind the arena card at negative
   * depth, so the readable play surface always wins, and the parallax layers
   * (clouds and two snow sheets) are the only animated pieces.
   */
  private createSkyBackdrop(palette: LevelPalette, levelId: number): void {
    const skyHigh = mixColor(palette.background, 0x2f7fc4, 0.3);
    const skyLow = mixColor(palette.background, 0xffffff, 0.72);
    const snowWhite = 0xffffff;
    const snowShade = mixColor(palette.rim, 0x6fa8d6, 0.35);
    const farRock = mixColor(palette.background, 0xffffff, 0.42);
    const pineBody = mixColor(palette.rim, 0x1f6b57, 0.62);

    // The diorama is screen scenery, not another kilometre-wide world layer.
    // Pinning every backdrop sheet to the camera prevents later rooms from
    // exposing the camera clear colour at the edge of the original 1280px sky.
    const sky = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-40);
    sky.fillGradientStyle(skyHigh, skyHigh, skyLow, skyLow, 1, 1, 1, 1);
    sky.fillRect(0, 0, BACKDROP_WIDTH, BACKDROP_HEIGHT);

    // Warm sun tucked into the free corner beside the arena card.
    const sunX = levelId % 2 === 0 ? 1_178 : 96;
    sky.fillStyle(0xfff3c0, 0.16);
    sky.fillCircle(sunX, 108, 116);
    sky.fillStyle(0xfff6d2, 0.28);
    sky.fillCircle(sunX, 108, 78);
    sky.fillStyle(0xfff8dc, 0.95);
    sky.fillCircle(sunX, 108, 44);
    sky.fillStyle(0xffffff, 0.75);
    sky.fillCircle(sunX - 12, 96, 26);

    // Distant rounded peaks with bright snow caps.
    const farHills = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-38);
    const farCrest = curvedPoints(-90, 452, [
      [40, 344, 150, 366, 262, 458],
      [372, 340, 486, 318, 596, 448],
      [706, 330, 812, 350, 918, 456],
      [1_030, 344, 1_140, 356, 1_370, 430],
    ], 12);
    fillRidge(farHills, farCrest, farRock, 0.95);
    farHills.fillStyle(snowWhite, 0.75);
    farHills.fillPoints([
      ...curvedPoints(190, 404, [[214, 372, 240, 372, 262, 458]], 10),
      ...curvedPoints(262, 458, [[236, 404, 214, 400, 190, 404]], 10).slice(1),
    ], true);
    farHills.fillPoints([
      ...curvedPoints(524, 392, [[552, 344, 574, 348, 596, 448]], 10),
      ...curvedPoints(596, 448, [[566, 396, 546, 392, 524, 392]], 10).slice(1),
    ], true);
    farHills.fillPoints([
      ...curvedPoints(846, 400, [[872, 354, 896, 358, 918, 456]], 10),
      ...curvedPoints(918, 456, [[888, 400, 868, 398, 846, 400]], 10).slice(1),
    ], true);

    // Near hills carry the pine treeline and read as solid snow.
    const nearHills = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-36);
    const nearCrest = curvedPoints(-100, 540, [
      [70, 430, 190, 460, 320, 566],
      [452, 468, 566, 472, 668, 560],
      [800, 444, 918, 462, 1_024, 564],
      [1_146, 464, 1_246, 462, 1_380, 528],
    ], 12);
    fillRidge(nearHills, nearCrest, mixColor(snowWhite, palette.rim, 0.26), 1);
    nearHills.fillStyle(snowShade, 0.28);
    nearHills.fillEllipse(320, 620, 420, 120);
    nearHills.fillEllipse(1_010, 626, 380, 110);

    const forest = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-35);
    for (let index = 0; index < 22; index += 1) {
      const spread = (index * 137 + levelId * 53) % 1_400 - 60;
      const depth = 500 + ((index * 71 + levelId * 29) % 90);
      const scale = 0.62 + ((index * 37) % 40) / 100;
      drawPine(forest, spread, depth, scale, pineBody, snowWhite);
    }

    // Foreground snow field with drifts framing the arena.
    const ground = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-34);
    fillRidge(ground, curvedPoints(-80, 604, [
      [80, 566, 210, 588, 340, 630],
      [500, 596, 700, 596, 880, 634],
      [1_040, 600, 1_180, 592, 1_380, 618],
    ], 12), snowWhite, 1);
    ground.fillStyle(snowShade, 0.2);
    ground.fillEllipse(210, 690, 460, 90);
    ground.fillEllipse(1_080, 700, 480, 96);
    ground.fillStyle(snowWhite, 1);
    ground.fillEllipse(120, 664, 260, 70);
    ground.fillEllipse(1_178, 656, 240, 66);
    ground.fillStyle(0xffffff, 0.9);
    for (let index = 0; index < 26; index += 1) {
      const x = (index * 197 + levelId * 61) % BACKDROP_WIDTH;
      const y = 612 + ((index * 43 + levelId * 17) % 96);
      ground.fillCircle(x, y, 1.6 + (index % 3));
    }

    // Two chunky cloud banks drift slowly across the sky margins.
    const cloudsFar = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-39);
    const cloudsNear = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-37);
    const farCloudSpots = [[110, 92, 0.8], [420, 62, 0.62], [760, 78, 0.7], [1_060, 210, 0.66]] as const;
    for (const [x, y, scale] of farCloudSpots) cloudPuff(cloudsFar, x, y, scale, 0xffffff, 0.62);
    const nearCloudSpots = [[240, 168, 1.05], [880, 140, 0.9], [1_180, 320, 0.8], [60, 330, 0.85]] as const;
    for (const [x, y, scale] of nearCloudSpots) {
      cloudPuff(cloudsNear, x, y + 6, scale, mixColor(0xffffff, palette.rim, 0.22), 0.9);
      cloudPuff(cloudsNear, x, y, scale, 0xffffff, 0.98);
    }
    this.backdropClouds = [cloudsFar, cloudsNear];

    // Snowfall: two deterministic sheets tweened downward on a loop.
    const snowFar = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-32);
    const snowNear = this.add.graphics().setScrollFactor(0).setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setDepth(-31);
    for (let index = 0; index < 54; index += 1) {
      const x = (index * 197 + levelId * 109 + 37) % BACKDROP_WIDTH;
      const y = (index * 83 + levelId * 53 + 19) % BACKDROP_HEIGHT;
      const outsideBoard = x < 206 || x > 1_074 || y < 84 || y > 654;
      const alpha = outsideBoard ? 0.75 : 0.24;
      drawBackdropFlake(snowFar, x, y, index % 9 === 0 ? 2.4 : 1.5, 0xffffff, alpha);
      drawBackdropFlake(snowFar, x, y - BACKDROP_HEIGHT, index % 9 === 0 ? 2.4 : 1.5, 0xffffff, alpha);
    }
    for (let index = 0; index < 30; index += 1) {
      const x = (index * 263 + levelId * 71 + 83) % BACKDROP_WIDTH;
      const y = (index * 127 + levelId * 43 + 11) % BACKDROP_HEIGHT;
      const outsideBoard = x < 202 || x > 1_078 || y < 86 || y > 656;
      const alpha = outsideBoard ? 0.95 : 0.3;
      const radius = index % 6 === 0 ? 4 : 2.6;
      drawBackdropFlake(snowNear, x, y, radius, 0xffffff, alpha);
      drawBackdropFlake(snowNear, x, y - BACKDROP_HEIGHT, radius, 0xffffff, alpha);
    }
    this.backdropSnow = [snowFar, snowNear];
    this.backdropMotionReduced = null;
    this.configureBackdropMotion(saveStore.data.settings.reducedMotion);
  }

  private configureBackdropMotion(reducedMotion: boolean): void {
    if (this.backdropMotionReduced === reducedMotion) return;
    this.backdropMotionReduced = reducedMotion;
    this.backdropTweens.forEach((tween) => tween.stop());
    this.backdropTweens = [];
    this.backdropClouds.forEach((clouds) => clouds.setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setAlpha(1));
    this.backdropSnow.forEach((snow) => snow.setPosition(BACKDROP_FIXED_X, BACKDROP_FIXED_Y).setAlpha(1));
    if (reducedMotion) return;
    this.backdropClouds.forEach((clouds, index) => {
      this.backdropTweens.push(this.tweens.add({
        targets: clouds,
        x: BACKDROP_FIXED_X + (index === 0 ? 46 : -58),
        y: BACKDROP_FIXED_Y + (index === 0 ? 5 : -4),
        duration: index === 0 ? 21_000 : 15_000,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1,
      }));
    });
    this.backdropSnow.forEach((snow, index) => {
      this.backdropTweens.push(this.tweens.add({
        targets: snow,
        y: BACKDROP_FIXED_Y + BACKDROP_HEIGHT,
        duration: index === 0 ? 24_000 : 14_000,
        ease: 'Linear',
        repeat: -1,
      }));
    });
  }
}

export function defaultRunData(levelId: number): StartSelection {
  return { levelId, players: 1, flavors: ['vanilla', 'strawberry'] };
}
